// Thin client over the engine's admin API (/v1/admin/...). Every call carries the stored
// admin bearer token. A 401 throws Unauthorized so the app can bounce back to the login gate.
import { getCreds, clearCreds } from './session';

export class Unauthorized extends Error {}
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const creds = getCreds();
  if (!creds) throw new Unauthorized('not signed in');
  const res = await fetch(`${creds.baseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${creds.token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    clearCreds();
    throw new Unauthorized('session expired');
  }
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const msg = (body && typeof body === 'object' && 'error' in body && typeof (body as any).error === 'string')
      ? (body as any).error
      : `request failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return body as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) });
}

/** Verify a candidate base URL + token by hitting the overview endpoint. */
export async function verifyCreds(baseUrl: string, token: string): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl}/v1/admin/overview`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Response shapes (mirror apps/api/src/admin-console.ts + the ENGINE_5 admin routes).
// ---------------------------------------------------------------------------
export interface Overview {
  applicationsInReview: number;
  members: number;
  moderationOpen: number;
  couponReview: number;
  proofToConfirm: number;
  liabilityCents: number;
  nextBatchDateIso: string;
}

export interface Ownership {
  found: boolean;
  ordersCount: number;
  hydrusBoardsBought: number;
  firstPurchaseIso: string | null;
}

export interface FraudChip {
  code: string;
  label: string;
}

export interface ApplicationItem {
  id: string;
  memberId: string | null;
  email: string | null;
  program: string;
  status: string;
  submittedAtIso: string;
  reviewer: string | null;
  reviewedAtIso: string | null;
  decisionNote: string | null;
  ownership: Ownership | null;
  ownershipFlag: boolean;
  ownershipProof: string | null;
  answers: Record<string, unknown> | null;
  // ENGINE_6
  ownershipVerified: boolean;
  autoApproved: boolean;
  autoApproveReason: string | null;
  undoUntilIso: string | null;
  proofSource: string | null;
  proofKind: string | null;
  fraudSignals: FraudChip[];
  // ADMIN_2 display fields (admin only)
  applicantName: string;
  memberAlias: string | null;
  memberCrewNumber: string | null;
}

export interface MemberListItem {
  id: string;
  name: string;
  realName: string | null;
  alias: string | null;
  email: string | null;
  program: string;
  accountStatus: string;
  lifecycleStatus: string;
  sharingToolsOn: boolean;
  crewNumber: string | null;
  code: string | null;
  joinDateIso: string;
  w9Status: string;
  referralCount: number;
  lastActivityIso: string | null;
  activeIn30d: boolean;
}

export interface MemberSale {
  id: string;
  orderName: string | null;
  productType: string | null;
  amountCents: number;
  status: string;
  holdUntilIso: string;
  processedAtIso: string;
  attributionType: string;
  referringSite: string | null;
  paidAtIso: string | null;
  paidMethod: string | null;
}

export interface MemberPayout {
  id: string;
  totalCents: number;
  method: string;
  status: string;
  createdAtIso: string;
  sentAtIso: string | null;
  failureReason: string | null;
}

export interface MemberHistory {
  id: string;
  entityType: string;
  action: string;
  actor: string;
  before: unknown;
  after: unknown;
  atIso: string;
}

export interface OwnerAccessDetail {
  source: 'order' | 'proof' | 'manual';
  orderId: string | null;
  boardTitle: string | null;
  proofAppId: string | null;
  grantedBy: string;
  grantedReason: string | null;
  grantedAtIso: string;
}

export interface MemberDetail {
  id: string;
  name: string;
  firstName: string | null;
  alias: string | null;
  email: string | null;
  program: string;
  // ENGINE_10
  tier: 'paddler' | 'owner' | 'household';
  ownerAccess: OwnerAccessDetail | null;
  householdMembers: { memberId: string; name: string; joinedAtIso: string }[];
  householdOwner: { memberId: string; name: string } | null;
  accountStatus: string;
  lifecycleStatus: string;
  sharingToolsOn: boolean;
  crewNumber: string | null;
  code: string | null;
  link: string | null;
  joinDateIso: string;
  w9Status: string;
  payoutMethod: string;
  storeCreditBalanceCents: number;
  seasonReferrals: number;
  seasonPaddleDays: number;
  sales: MemberSale[];
  payouts: MemberPayout[];
  history: MemberHistory[];
}

interface Bucketed {
  heldCents: number;
  approvedCents: number;
  paidCents: number;
}
export interface Money {
  totals: Bucketed & { liabilityCents: number };
  byMonth: (Bucketed & { month: string })[];
  byProgram: (Bucketed & { program: string })[];
}

export interface CouponLine {
  id: string;
  orderName: string | null;
  productType: string | null;
  amountCents: number;
  memberId: string;
  memberName: string;
  program: string;
  referringSite: string | null;
  landingSite: string | null;
  processedAtIso: string;
}

export interface Adjustment {
  id: string;
  commissionLineId: string;
  type: string;
  deltaCents: number;
  reason: string;
  createdBy: string;
  flagged: boolean;
  atIso: string;
  orderName: string | null;
  memberId: string;
  memberName: string;
}

export interface PayoutPreview {
  executionEnabled: boolean;
  minPayoutCents: number;
  totalCents: number;
  storeCreditCents: number;
  cashCents: number;
  payDateIso: string;
  periodLabel: string;
  items: { memberId: string; name: string; method: string; amountCents: number; lineCount: number }[];
  belowMinimum: { memberId: string; name: string; amountCents: number }[];
}

export interface ModerationPost {
  id: string;
  memberId: string;
  authorName: string;
  authorCrewNumber: string | null;
  authorAccountStatus: string;
  type: string;
  caption: string | null;
  spotName: string | null;
  status: string;
  visibility: string;
  reportCount: number;
  commentCount: number;
  createdAtIso: string;
}

// ENGINE_9 Game tab.
export interface GameChallengeAdmin {
  id: string;
  period: string;
  title: string;
  metric: 'days' | 'miles' | 'hours';
  goal: number;
  prize: string | null;
  crewProgress: number;
  finishers: number;
}
export interface GameTeamGoalAdmin {
  id: string;
  period: string;
  title: string;
  metric: 'miles' | 'days' | 'hours';
  goal: number;
  progress: number;
}
export interface GameFlagged {
  paddleId: string;
  memberId: string;
  memberName: string;
  crewNumber: string | null;
  dateIso: string;
  miles: number;
  avgMph: number;
  maxMph: number;
  reason: string | null;
  reviewStatus: string;
}
export interface GameTab {
  period: string;
  challenge: GameChallengeAdmin | null;
  teamGoal: GameTeamGoalAdmin | null;
  flagged: GameFlagged[];
}

export interface Reports {
  year: number;
  thresholdCents: number;
  activeShare: { activeCount: number; totalCount: number; fraction: number };
  dormant: { id: string; name: string; program: string; crewNumber: string | null; lastActivityIso: string | null; lifecycleStatus: string }[];
  topNewProducers: { id: string; name: string; program: string; joinDateIso: string; referralCount: number }[];
  demoActivity: { id: string; name: string; crewNumber: string | null; referralCount: number; fleetSize: number; lastActivityIso: string | null; lifecycleStatus: string }[];
  taxTracker: { id: string; name: string; paidYtdCents: number; w9Status: string; over1099Threshold: boolean }[];
}
